/*
 * S Brillet - Market-aware conversion for hardcoded USD storefront copy.
 *
 * Native Shopify product/cart/variant prices are intentionally NOT modified.
 * Customer-facing hardcoded USD text is converted with Shopify.currency.rate.
 * This includes values entered from Theme Customizer settings, even when those
 * settings are re-rendered live inside the Shopify theme editor.
 *
 * Explicit markers:
 *   [data-sbrillet-market-money]
 *   [data-sbrillet-market-money-scope]
 * always opt content into conversion, even if the element looks price-related.
 */
(function () {
  'use strict';

  var CONFIG = window.SBrilletMarketMoneyConfig || {};
  var processedTextNodes = new WeakMap();
  var observer = null;
  var scheduled = false;

  // Supports both common Theme Customizer forms:
  //   $1,234.56 / $100 / $0.99
  //   1,234.56$ / 100$ / 0.99$
  // Trailing copy such as +, OFF, Sparkles, etc. stays untouched.
  var USD_AMOUNT_RE = /\$\s*(\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)|(\d{1,3}(?:,\d{3})*(?:\.\d+)?|\d+(?:\.\d+)?)\s*\$/g;

  var EXPLICIT_SELECTOR = '[data-sbrillet-market-money-scope], [data-sbrillet-market-money]';

  // These containers normally hold prices already contextualized by Shopify
  // Markets. They are skipped during the automatic/global pass to prevent a
  // second conversion in currencies that also use a "$" symbol (CAD/AUD, etc.).
  // An explicit data-sbrillet-market-money marker always overrides this skip.
  var NATIVE_PRICE_SELECTOR = [
    '[data-pcard-price]',
    '[data-product-price]',
    '[data-variant-price]',
    '[data-cart-subtotal-price]',
    '[data-total-price]:not([data-sbrillet-market-money])',
    '[data-price-per-item]',
    'price-per-item',
    '.product-block-group--price',
    '.m-product-card__price',
    '.m-lookbook-product__price',
    '.m-search-item--price',
    '.m-cart-item__price',
    '.m-cart__subtotal--price',
    '.m-gift-wrapping--price',
    '.variant-item__price',
    '.variant-item__details-price',
    '.product-bundles__item-prices',
    '.m-quick-order-list__subtotal-price',
    '.cps-price-row',
    '.bsd-card__price',
    '[class*="sbs-price-row-"]',
    '[class*="price-per-item"]',
    '[class*="price__regular"]',
    '[class*="price__sale"]',
    '[class*="m-price-item"]',
    '[class*="subtotal-price"]',
    '[class*="total-price"]'
  ].join(',');

  function getCurrencyState() {
    var shopifyCurrency = window.Shopify && window.Shopify.currency ? window.Shopify.currency : {};
    var rate = Number(shopifyCurrency.rate);

    return {
      base: String(CONFIG.baseCurrency || 'USD').toUpperCase(),
      active: String(shopifyCurrency.active || CONFIG.activeCurrency || 'USD').toUpperCase(),
      rate: Number.isFinite(rate) && rate > 0 ? rate : 1
    };
  }

  function getCurrencySignature() {
    var state = getCurrencyState();
    return [state.base, state.active, String(state.rate)].join('|');
  }

  function formatMoneyFallback(cents, currency) {
    var amount = cents / 100;
    var rawLocale = String(CONFIG.locale || document.documentElement.lang || 'en').replace('_', '-');
    var language = rawLocale.split('-')[0] || 'en';
    var country = String(CONFIG.countryCode || '').toUpperCase();
    var locale = country ? language + '-' + country : rawLocale;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol'
      }).format(amount);
    } catch (error) {
      return currency + ' ' + amount.toFixed(2);
    }
  }

  function formatPresentmentMoney(cents, currency) {
    // Prefer Intl here because shop.money_format can still carry the store-base
    // symbol. Intl uses the active Shopify Markets currency explicitly.
    var fallback = formatMoneyFallback(cents, currency);

    // If the active currency is the shop base currency, preserving the theme's
    // native money formatting is safe and keeps storefront formatting familiar.
    var state = getCurrencyState();
    if (state.active === state.base && typeof window.formatMoney === 'function' && window.MinimogSettings && window.MinimogSettings.money_format) {
      try {
        return window.formatMoney(cents, window.MinimogSettings.money_format);
      } catch (error) {}
    }

    return fallback;
  }

  function convertUsdText(sourceText) {
    if (!sourceText || sourceText.indexOf('$') === -1) return sourceText;

    var state = getCurrencyState();

    // Hardcoded storefront amounts in this theme are authored in USD. The
    // store currently uses USD as the Shopify base currency, so the Markets
    // storefront rate converts those values to the active presentment currency.
    if (state.base !== 'USD') return sourceText;

    return sourceText.replace(USD_AMOUNT_RE, function (fullMatch, prefixAmount, suffixAmount) {
      var numericPart = prefixAmount || suffixAmount;
      var usdAmount = Number(String(numericPart).replace(/,/g, ''));
      if (!Number.isFinite(usdAmount)) return fullMatch;

      var presentmentCents = Math.round(usdAmount * 100 * state.rate);
      return formatPresentmentMoney(presentmentCents, state.active);
    });
  }

  function closestSafe(element, selector) {
    if (!element || !element.closest) return null;
    try {
      return element.closest(selector);
    } catch (error) {
      return null;
    }
  }

  function isExplicitMoneyTextNode(node) {
    var parent = node && node.parentElement;
    return !!(parent && closestSafe(parent, EXPLICIT_SELECTOR));
  }

  function isIgnoredTextNode(node) {
    var parent = node && node.parentElement;
    if (!parent) return true;

    if (closestSafe(parent, 'script, style, noscript, template, textarea, input, select, option, code, pre')) {
      return true;
    }

    // Explicitly-marked text is always allowed to convert.
    if (isExplicitMoneyTextNode(node)) return false;

    // Avoid re-converting actual Shopify prices during the automatic pass.
    if (closestSafe(parent, NATIVE_PRICE_SELECTOR)) return true;

    return false;
  }

  function processTextNode(node) {
    if (!node || node.nodeType !== Node.TEXT_NODE || isIgnoredTextNode(node)) return;

    var current = node.nodeValue || '';
    var previous = processedTextNodes.get(node);
    var signature = getCurrencySignature();
    var source = current;

    // If this node was already converted and the market/currency did not change,
    // do nothing. If the currency did change without a full page reload, reuse
    // the original USD source and re-convert it for the new market.
    if (previous && previous.converted === current) {
      if (previous.signature === signature) return;
      source = previous.source;
    } else if (current.indexOf('$') === -1) {
      return;
    }

    var converted = convertUsdText(source);
    if (converted === current) {
      processedTextNodes.set(node, { source: source, converted: current, signature: signature });
      return;
    }

    processedTextNodes.set(node, { source: source, converted: converted, signature: signature });
    node.nodeValue = converted;
  }

  function processElement(root) {
    if (!root) return;

    if (root.nodeType === Node.TEXT_NODE) {
      processTextNode(root);
      return;
    }

    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
      return;
    }

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) processTextNode(node);
  }

  function processAllMoney() {
    if (!document.body) return;

    // First process explicit scopes, then perform the safe automatic pass across
    // visible storefront text. The latter is what makes Theme Customizer text
    // such as "$1 = 1.25 Sparkles" work without manually adding a marker to each
    // future setting field.
    document.querySelectorAll(EXPLICIT_SELECTOR).forEach(function (el) {
      processElement(el);
    });
    processElement(document.body);
  }

  function scheduleFullPass() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      processAllMoney();
    });
  }

  function startObserver() {
    if (observer || !document.body) return;

    observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'characterData') {
          processTextNode(mutation.target);
          return;
        }

        mutation.addedNodes.forEach(function (added) {
          processElement(added);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function init() {
    processAllMoney();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Shopify theme-editor / dynamically re-rendered section support.
  [
    'shopify:section:load',
    'shopify:section:reorder',
    'shopify:section:select',
    'shopify:block:select',
    'localization:changed',
    'currency:changed',
    'shopify:currency:change'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, scheduleFullPass);
  });

  window.addEventListener('pageshow', scheduleFullPass);

  window.SBrilletMarketMoney = {
    refresh: scheduleFullPass,
    convertUsdText: convertUsdText
  };
})();
